import React, { useMemo, useState } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { compare } from "dinero.js"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle, faPen, faTrashCan } from "@fortawesome/pro-light-svg-icons"
import { Stack, Typography, IconButton, Box, Tooltip, Button } from "@mui/material"

import { toDinero, toCurrency, zeroDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { selectEffectiveUserType } from "../../../../../../modules/context/contextSelectors"
import { UserType } from "../../../../../../modules/login/loginTypes"
import { useAppSelector } from "../../../../../../modules/store/storeHooks"
import { MeetingServicePriceListState } from "../../../createOrderTypes"
import { ProductsTableItem } from "../../../../../../modules/merchant/admin/products/productsTypes"
import { DeliveryOrderLine, DynamicConfigKey, isProductWishOrderLine, ProductWishOrderLine } from "../../form/formTypes"
import { useCanManageAdminOnlyProducts } from "../../../hooks/useCanManageAdminOnlyProducts"
import { ProductCounter } from "../../shop/product/ProductCounter"
import { ProductUpdateQuantityProps } from "./MeetingServices"
import { useProductStock } from "../../../../productAvailability/useProductStock"
import { getExtraServiceBreakdown } from "../extraServices/extraServiceBreakdown"

const useStyles = makeStyles()((theme) => ({
    textEllipsis: {
        textAlign: "left",
    },
    button: {
        padding: 0,
        color: theme.palette.tailwind.slate[400],
        transition: "all 0.3s ease-out",
        "&:hover": {
            color: theme.palette.tailwind.red[400],
        },
    },
    counterWrapper: {
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        paddingRight: "2rem",
    },
    floatingDelete: {
        position: "absolute",
        right: "0",
        top: "50%",
        transform: "translateY(-50%)",
        opacity: 0,
        transition: "opacity 0.3s ease-in-out",
        "&.visible": {
            opacity: 1,
        },
    },
    infoIcon: {
        color: "red",
    },
    lockedIcon: {
        color: theme.palette.tailwind.slate[400],
    },
    tooltipPopper: {
        zIndex: 9999,
    },
    editIcon: {
        padding: 0,
        color: theme.palette.tailwind.slate[400],
        transition: "color 0.3s ease-out",
        "&:hover": {
            color: theme.palette.tailwind.sky[500],
        },
    },
    setPriceButton: {
        padding: ".25rem .75rem",
        color: theme.palette.tailwind.amber[700],
        border: `1px solid ${theme.palette.tailwind.amber[400]}`,
        borderRadius: ".5rem",
        fontWeight: 500,
        whiteSpace: "nowrap",
        transition: "all 0.3s ease-out",
        "&:hover": {
            background: theme.palette.tailwind.amber[50],
        },
    },
    priceWrapper: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".5rem",
    },
}))

type BreakdownLabelProps = {
    seatCount: number
    pricePerSeat: Parameters<typeof toDinero>[0] | undefined
    minPrice: Parameters<typeof toDinero>[0] | undefined
    maxPrice: Parameters<typeof toDinero>[0] | undefined
    quantity: number
    moneyFactory: { format: (value: ReturnType<typeof toDinero>) => string }
}

function renderExtraServiceBreakdownLabel({
    seatCount,
    pricePerSeat,
    minPrice,
    maxPrice,
    quantity,
    moneyFactory,
}: BreakdownLabelProps): React.ReactNode {
    const bd = getExtraServiceBreakdown(seatCount, pricePerSeat, minPrice, maxPrice)
    if (bd?.kind === "seats") {
        return (
            <LocalizedStrict
                id="create-order-extra-services-seats-breakdown"
                vars={{ count: seatCount, price: moneyFactory.format(toDinero(bd.pricePerSeat)) }}
            >
                <Typography variant="subtitle1" fontWeight={400} color="text.secondary">
                    {seatCount} seats x {moneyFactory.format(toDinero(bd.pricePerSeat))}
                </Typography>
            </LocalizedStrict>
        )
    }
    if (bd?.kind === "min") {
        return (
            <LocalizedStrict
                id="create-order-extra-services-min-price"
                vars={{ price: moneyFactory.format(toDinero(bd.minPrice)) }}
            >
                <Typography variant="subtitle1" fontWeight={400} color="text.secondary">
                    Min. price {moneyFactory.format(toDinero(bd.minPrice))}
                </Typography>
            </LocalizedStrict>
        )
    }
    if (bd?.kind === "max") {
        return (
            <LocalizedStrict
                id="create-order-extra-services-max-price"
                vars={{ price: moneyFactory.format(toDinero(bd.maxPrice)) }}
            >
                <Typography variant="subtitle1" fontWeight={400} color="text.secondary">
                    Max. price {moneyFactory.format(toDinero(bd.maxPrice))}
                </Typography>
            </LocalizedStrict>
        )
    }
    return <Typography variant="subtitle1">{quantity}</Typography>
}

type Props = {
    product: DeliveryOrderLine
    catalogProduct?: ProductsTableItem
    catalogProducts?: Map<number, ProductsTableItem>
    deliveryTime: string
    isRefundOrder: boolean
    onUpdateQuantity: (props: ProductUpdateQuantityProps) => void
    onEditWish: (deliveryTime: string, line: ProductWishOrderLine) => void
    isEditable: boolean
    availableProductIds: Set<number>
}

export const MeetingServiceProduct = ({
    product,
    catalogProduct,
    catalogProducts,
    deliveryTime,
    isRefundOrder,
    onUpdateQuantity,
    onEditWish,
    isEditable,
    availableProductIds,
}: Props) => {
    const { classes } = useStyles()
    const [isHovering, setIsHovering] = useState(false)
    const effectiveUserType = useAppSelector(selectEffectiveUserType)
    const isStaff = effectiveUserType !== UserType.USER
    const [{ value: priceListState }] = useField<MeetingServicePriceListState>("priceList")
    const [{ value: seatCountValue }] = useField(DynamicConfigKey.NUMBER_OF_SEATS)
    const seatCount: number = seatCountValue || 0
    const priceListItem: MeetingServicePriceListState["item"] = priceListState.item
    const productWishLine = isProductWishOrderLine(product) ? product : null
    const isProductWish = productWishLine !== null
    const isAutoAddedExtra = !isProductWishOrderLine(product) && !!product.isAutoAddedExtraService
    const canEditWishPrice = isStaff && isEditable && productWishLine !== null

    // Staff-only lines stay visible (and keep counting towards the total) for everyone, but only
    // the merchant may change them. The catalog fallback covers orders whose lines predate the
    // backend sending isAdminOnly — catalogProducts is built from the unfiltered catalog.
    const canManageAdminOnly = useCanManageAdminOnlyProducts()
    const isAdminOnlyLine =
        !isProductWishOrderLine(product) && (product.isAdminOnly ?? catalogProduct?.isAdminOnly ?? false)
    const isLineLocked = isAdminOnlyLine && !canManageAdminOnly
    const canEditLine = isEditable && !isLineLocked
    // Once the merchant has quoted a wish, that price covers the exact name and quantity it was
    // submitted with, so the user may no longer change the quantity — an unpriced wish is still their
    // own draft. `price` is a MonetaryAmount object, so a 0 kr quote counts as priced as well. The user
    // can still drop the whole line and add a new wish.
    const isWishQuantityLocked = !isStaff && productWishLine?.price != null
    const canEditQuantity = canEditLine && !isWishQuantityLocked
    // Only explain the missing counter where one would otherwise be shown — a fully read-only summary
    // has no counters at all, so the hint would be noise there.
    const showWishQuantityLockedHint = canEditLine && isWishQuantityLocked
    const showEditWarnings = canEditLine && !isRefundOrder && !product.isExtraService

    const isMismatchedPriceList = useMemo(() => {
        if (isProductWish || isRefundOrder) return false
        const pId = product.priceListId
        const currentId = priceListItem?.priceListId
        return typeof pId === "number" && typeof currentId === "number" && pId !== currentId
    }, [isProductWish, product.priceListId, priceListItem?.priceListId, isRefundOrder])

    const isProductInShops = useMemo(() => {
        if (isProductWish || isRefundOrder) return true
        return typeof product.id === "number" && availableProductIds.has(product.id)
    }, [availableProductIds, isProductWish, isRefundOrder, product.id])

    const resolvedStock = useMemo(() => {
        if (isProductWish || isRefundOrder) return undefined
        return catalogProduct?.stock ?? ("stock" in product ? product.stock : undefined)
    }, [isProductWish, isRefundOrder, product, catalogProduct])

    const { disableIncrease: isStockLimitReached } = useProductStock({
        stock: resolvedStock,
        currentQuantity: product.quantity,
    })

    const moneyFactory = useMoney({})

    const breakdownLabel = renderExtraServiceBreakdownLabel({
        seatCount,
        pricePerSeat: product.extraServicePricePerSeat,
        minPrice: product.extraServiceMinPrice,
        maxPrice: product.extraServiceMaxPrice,
        quantity: product.quantity,
        moneyFactory,
    })
    const totalPrice = product.price
        ? toDinero({
              amount: product.price.amount * product.quantity,
              scale: product.price.scale,
              currency: product.price.currency,
          })
        : null

    const isTotalPriceZero = useMemo(() => {
        if (!totalPrice) return true
        const zero = zeroDinero(toCurrency(product.price!.currency))
        return compare(totalPrice, zero) === 0
    }, [totalPrice, product.price])

    return (
        <>
            <Stack justifyContent="space-between" width="100%" gap="0.5rem">
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap=".5rem">
                    <Typography variant="body1" className={classes.textEllipsis}>
                        {product.name}
                    </Typography>
                    {(showEditWarnings || isLineLocked || showWishQuantityLockedHint) && (
                        <Stack gap=".5rem" flexDirection="row">
                            {showEditWarnings && isMismatchedPriceList && (
                                <LocalizedStrict
                                    id="create-order-order-summary-meeting-services-product-warning-price-list-tooltip"
                                    attrs={{ title: true }}
                                >
                                    <Tooltip
                                        placement="left"
                                        arrow
                                        title="The selected price list does not match the product price list — increase is not available."
                                        classes={{ popper: classes.tooltipPopper }}
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} className={classes.infoIcon} size="lg" />
                                    </Tooltip>
                                </LocalizedStrict>
                            )}
                            {showEditWarnings && !isProductInShops && (
                                <LocalizedStrict
                                    id="create-order-order-summary-meeting-services-product-warning-missing-product-tooltip"
                                    attrs={{ title: true }}
                                >
                                    <Tooltip
                                        placement="left"
                                        arrow
                                        title="This product is not available in the selected shop list"
                                        classes={{ popper: classes.tooltipPopper }}
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} className={classes.infoIcon} size="lg" />
                                    </Tooltip>
                                </LocalizedStrict>
                            )}
                            {isLineLocked && (
                                <LocalizedStrict
                                    id="create-order-order-summary-meeting-services-product-staff-only-tooltip"
                                    attrs={{ title: true }}
                                >
                                    <Tooltip
                                        placement="left"
                                        arrow
                                        title="Only the merchant can change this item"
                                        classes={{ popper: classes.tooltipPopper }}
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} className={classes.lockedIcon} size="lg" />
                                    </Tooltip>
                                </LocalizedStrict>
                            )}
                            {showWishQuantityLockedHint && (
                                <LocalizedStrict
                                    id="create-order-order-summary-meeting-services-wish-locked-quantity-tooltip"
                                    attrs={{ title: true }}
                                >
                                    <Tooltip
                                        placement="left"
                                        arrow
                                        title="Only the merchant can change the number of items in a product wish"
                                        classes={{ popper: classes.tooltipPopper }}
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} className={classes.lockedIcon} size="lg" />
                                    </Tooltip>
                                </LocalizedStrict>
                            )}
                        </Stack>
                    )}

                    {/* {!isRefundOrder && (
                        <IconButton
                            onClick={() =>
                                onUpdateQuantity({
                                    deliveryTime,
                                    productId: product.id,
                                    quantity: 0,
                                    isExtraService: product.isExtraService,
                                })
                            }
                            className={classes.button}
                        >
                            <FontAwesomeIcon icon={faTrashCan} size="xs" />
                        </IconButton>
                    )} */}
                </Stack>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-end"
                    gap=".5rem"
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                >
                    {product.isExtraService ? (
                        // Breakdown label replaces the quantity counter
                        <Stack direction="row" alignItems="center" gap=".5rem">
                            {breakdownLabel}
                            {canEditLine && (isRefundOrder || !isAutoAddedExtra) && (
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        onUpdateQuantity({
                                            deliveryTime,
                                            productId: product.id,
                                            quantity: 0,
                                            isExtraService: product.isExtraService,
                                        })
                                    }
                                    className={classes.button}
                                >
                                    <FontAwesomeIcon icon={faTrashCan} size="xs" />
                                </IconButton>
                            )}
                        </Stack>
                    ) : (
                        <Box className={classes.counterWrapper}>
                            {canEditQuantity ? (
                                <ProductCounter
                                    count={product.quantity}
                                    handleCounter={(newQty) => {
                                        if (newQty !== product.quantity) {
                                            onUpdateQuantity({
                                                deliveryTime,
                                                productId: product.id,
                                                quantity: newQty,
                                                isExtraService: product.isExtraService,
                                                stock: resolvedStock,
                                                catalogProducts,
                                            })
                                        }
                                    }}
                                    disableIncrease={isStockLimitReached}
                                    blockIncrease={isMismatchedPriceList || !isProductInShops}
                                    onDisabledClick={() =>
                                        onUpdateQuantity({
                                            deliveryTime,
                                            productId: product.id,
                                            quantity: product.quantity + 1,
                                            isExtraService: product.isExtraService,
                                            stock: resolvedStock,
                                            catalogProducts,
                                        })
                                    }
                                    allowZeroDisplay={isRefundOrder}
                                />
                            ) : (
                                <LocalizedStrict
                                    id={
                                        product.quantity > 1
                                            ? "create-order-order-summary-meeting-services-product-items"
                                            : "create-order-order-summary-meeting-services-product-item"
                                    }
                                    vars={{ count: product.quantity }}
                                >
                                    <Typography>
                                        {product.quantity} {product.quantity > 1 ? "items" : "item"}
                                    </Typography>
                                </LocalizedStrict>
                            )}

                            {(!isRefundOrder || isProductWish) && canEditLine && (
                                <IconButton
                                    onClick={() =>
                                        onUpdateQuantity({
                                            deliveryTime,
                                            productId: product.id,
                                            quantity: 0,
                                            isExtraService: product.isExtraService,
                                            isProductWish,
                                        })
                                    }
                                    className={`${classes.button} ${classes.floatingDelete} ${
                                        isHovering ? "visible" : ""
                                    }`}
                                    size="small"
                                >
                                    <FontAwesomeIcon icon={faTrashCan} size="lg" />
                                </IconButton>
                            )}
                        </Box>
                    )}

                    {canEditWishPrice ? (
                        productWishLine.price ? (
                            <Box className={classes.priceWrapper}>
                                {totalPrice && (
                                    <Typography variant="subtitle1">
                                        {isRefundOrder && !isTotalPriceZero && "-"}
                                        {moneyFactory.formatAsNumber(totalPrice)}
                                    </Typography>
                                )}
                                <LocalizedStrict
                                    id="create-order-order-summary-meeting-services-wish-edit-price-button"
                                    attrs={{ title: true }}
                                >
                                    <Tooltip
                                        placement="left"
                                        arrow
                                        title="Edit price"
                                        classes={{ popper: classes.tooltipPopper }}
                                    >
                                        <IconButton
                                            className={classes.editIcon}
                                            onClick={() => onEditWish(deliveryTime, productWishLine)}
                                        >
                                            <FontAwesomeIcon icon={faPen} size="sm" />
                                        </IconButton>
                                    </Tooltip>
                                </LocalizedStrict>
                            </Box>
                        ) : (
                            <LocalizedStrict id="create-order-order-summary-meeting-services-wish-set-price-button">
                                <Button
                                    type="button"
                                    variant="outlined"
                                    className={classes.setPriceButton}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => onEditWish(deliveryTime, productWishLine)}
                                >
                                    Set price
                                </Button>
                            </LocalizedStrict>
                        )
                    ) : (
                        totalPrice && (
                            <Typography variant="subtitle1">
                                {isRefundOrder && !isTotalPriceZero && "-"}
                                {moneyFactory.formatAsNumber(totalPrice)}
                            </Typography>
                        )
                    )}
                </Stack>
            </Stack>
        </>
    )
}
